import React, { useEffect, useRef, useState } from 'react';
import { Card } from '@workday/canvas-kit-react/card';
import { autoId, MiddleEllipsisWithTooltip } from '@workday/upc-common';
import styles from './app-card.module.scss';
import { lockIcon } from '@workday/canvas-system-icons-web';
import { Popper } from '@workday/canvas-kit-react/popup';
import { Tooltip, OverflowTooltip } from '@workday/canvas-kit-react/tooltip';
import { colors } from '@workday/canvas-kit-react';
import { Application } from '../../../slices/account-api.slice';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { isEnvironmentProd } from '../../../utils/string';
import { TertiaryButton } from '@workday/canvas-kit-react/button';
import { ID_MY_APP_CARD } from '../../../autoIds';

type AppCardProps = {
    app: Application;
    onAppCardClick: (app: Application) => void;
};

export function AppCard({ app, onAppCardClick }: AppCardProps) {
    const {
        appId,
        tenantName,
        environment,
        environmentType,
        name,
        privacyUri,
        tosUri,
        vcrRestricted,
        userLocked,
        userExpired,
        isFedRAMP
    } = app;

    const { t } = useTranslation('account');
    const anchorRef = useRef();
    const infoIconPopper = useRef();
    const [isOpen, setIsOpen] = useState(false);
    const isNonProdEnvironment = environment && !isEnvironmentProd(environment);
    const disableCard = userLocked || userExpired;

    useEffect(() => {
        const onDOMClick = e => {
            e.stopPropagation();
            setIsOpen(false);
        };

        document.addEventListener('click', onDOMClick);
        return () => {
            document.removeEventListener('click', onDOMClick);
        };
    }, [setIsOpen]);

    const onLinkClick = e => {
        e.stopPropagation();
    };

    const onAppClick = () => {
        onAppCardClick(app);
    };

    const [cardStyle, sandBoxedWatermark] = app.sandBoxed
        ? [
              [styles.sandBoxed, app.expired ? styles.expired : null],
              <div className={classNames(styles.watermark)}>
                  <div className={classNames(styles.watermark__inner)}>
                      <div className={classNames(styles.watermark__body)}>
                          {app.expired ? t('AppCard.expired') : t('AppCard.sandbox')}
                      </div>
                  </div>
              </div>
          ]
        : [styles.appCard, <div></div>];

    return (
        <Card
            {...autoId(ID_MY_APP_CARD)}
            as='button'
            padding='xs'
            className={classNames(
                cardStyle,
                vcrRestricted ? styles.vcrRestricted : null,
                isNonProdEnvironment ? styles.hasEnvironment : null,
                disableCard ? styles.disableCard : null
            )}
            disabled={disableCard || app.expired}
            onClick={onAppClick}>
            <Card.Body>
                <OverflowTooltip>
                    <p className={styles.appName}>{name}</p>
                </OverflowTooltip>
                {isNonProdEnvironment && (
                    <>
                        <OverflowTooltip>
                            <span className={styles.environmentName}>{environment}</span>
                        </OverflowTooltip>
                        {environmentType && (
                            <OverflowTooltip>
                                <span className={styles.environmentType}>{environmentType}</span>
                            </OverflowTooltip>
                        )}
                    </>
                )}
                {isFedRAMP && (
                    <OverflowTooltip>
                        <span className={styles.fedRamp}>{'WGC'}</span>
                    </OverflowTooltip>
                )}
                {tenantName || vcrRestricted ? <hr /> : null}
                {tenantName && (
                    <div className={styles.tenantNameContainer}>
                        <MiddleEllipsisWithTooltip className={styles.tenantName} text={tenantName} />
                    </div>
                )}
                {sandBoxedWatermark}
                <div className={styles.cardIconsContainer}>
                    {vcrRestricted && <VcrIcon id={appId} />}
                    {/* TODO Temporarily remove the info button with links - will come back in a future PR
                    (privacyUri || tosUri) && (
                        // @ts-ignore
                        <div ref={anchorRef} className={styles.infoIcon}>
                            <TertiaryButton
                                {...autoId(ID_MY_APP_CARD_INFO_ICON_TEST)}
                                onClick={onInfoIconClick}
                                icon={infoIcon}
                                size={'small'}
                            />
                        </div>
                    )*/}
                </div>
                <Popper anchorElement={anchorRef.current} open={isOpen} placement='bottom-start'>
                    {/* @ts-ignore */}
                    <Card className={styles.popperCard} ref={infoIconPopper}>
                        <Card.Body>
                            {tosUri && (
                                <a href={tosUri} onClick={onLinkClick} rel='noopener noreferrer' target={'_blank'}>
                                    {t('AppCard.notices')}
                                </a>
                            )}
                            {privacyUri && (
                                <a href={privacyUri} onClick={onLinkClick} rel='noopener noreferrer' target={'_blank'}>
                                    {t('AppCard.policy')}
                                </a>
                            )}
                        </Card.Body>
                    </Card>
                </Popper>
            </Card.Body>
        </Card>
    );
}

const VcrIcon = id => {
    const { t } = useTranslation('account');
    const vcrMessage = t('AppCard.virtualCleanRoom');

    return (
        <Tooltip
            id={`vcrTooltip-${id}`}
            placement='bottom'
            title={<div className={styles.vcrRestrictedMessage}>{vcrMessage}</div>}>
            <TertiaryButton
                {...autoId('vcrRestricted')}
                as={'span'}
                tabIndex={0}
                icon={lockIcon}
                colors={{
                    default: {
                        icon: colors.blueberry400
                    },
                    hover: {
                        icon: colors.blueberry500
                    },
                    active: {},
                    focus: {},
                    disabled: {}
                }}
                size={'extraSmall'}
            />
        </Tooltip>
    );
};
